// API service that works both in Electron and Web
// In production, use relative URLs (same server serves API and frontend)
// In development, use localhost:3001
const host = window.location.hostname;
// Opened from another device on the home network (e.g. http://192.168.2.16:3000) → still dev:
// the API runs on that same computer, port 3001.
const isLanDev = /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host) || host.endsWith('.local');
const isProduction = host !== 'localhost' && host !== '127.0.0.1' && !isLanDev;
const API_BASE = isProduction
  ? ''
  : (process.env.REACT_APP_API_URL || (isLanDev ? `http://${host}:3001` : 'http://localhost:3001'));

// Check if running in Electron
const isElectron = () => {
  return window.electronAPI !== undefined;
};

// Helper: unwrap server responses that may use {data, error} envelope
// Returns the data array (or the raw response if it's already an array)
const unwrapArray = (json) => {
  if (Array.isArray(json)) return json;
  if (json && json.error) {
    console.warn('API warning:', json.error);
  }
  if (json && Array.isArray(json.data)) return json.data;
  return json;
};

// NHL API functions
export const api = {
  // Fetch NHL games for specific dates
  fetchNHLGames: async (dates) => {
    if (isElectron()) {
      return window.electronAPI.fetchNHLGames(dates);
    }
    const response = await fetch(`${API_BASE}/api/games?dates=${dates.join(',')}`);
    if (!response.ok) throw new Error('Failed to fetch games');
    return response.json();
  },

  // Fetch live scores
  fetchLiveScores: async () => {
    if (isElectron()) {
      return window.electronAPI.fetchLiveScores();
    }
    const response = await fetch(`${API_BASE}/api/live-scores`);
    if (!response.ok) throw new Error('Failed to fetch live scores');
    const json = await response.json();
    return unwrapArray(json);
  },

  // Fetch game details for bet settlement
  fetchGameDetails: async (gameId) => {
    if (isElectron()) {
      return window.electronAPI.fetchGameDetails(gameId);
    }
    const response = await fetch(`${API_BASE}/api/game-details/${gameId}`);
    if (!response.ok) throw new Error('Failed to fetch game details');
    return response.json();
  },

  // Fetch game stats
  fetchGameStats: async (gameId) => {
    if (isElectron()) {
      return window.electronAPI.fetchGameStats(gameId);
    }
    const response = await fetch(`${API_BASE}/api/game-stats/${gameId}`);
    if (!response.ok) throw new Error('Failed to fetch game stats');
    return response.json();
  },

  // Fetch injuries for a team
  fetchInjuries: async (teamAbbrev) => {
    if (isElectron()) {
      return window.electronAPI.fetchInjuries(teamAbbrev);
    }
    const response = await fetch(`${API_BASE}/api/injuries/${teamAbbrev}`);
    if (!response.ok) throw new Error('Failed to fetch injuries');
    return response.json();
  },

  // Fetch all team rosters
  // is the site being updated right now? (MAINTENANCE on the server)
  fetchStatus: async () => {
    const r = await fetch(`${API_BASE}/api/status`, { cache: 'no-store' });
    if (!r.ok && r.status !== 503) return { maintenance: false };
    return r.json();
  },

  fetchRosters: async () => {
    if (isElectron()) {
      return window.electronAPI.fetchRosters ? window.electronAPI.fetchRosters() : null;
    }
    const response = await fetch(`${API_BASE}/api/rosters?t=${Date.now()}`);
    if (!response.ok) throw new Error('Failed to fetch rosters');
    return response.json();
  },

  // The NHL player list, kept fresh while the server is still building it (right after a
  // restart it takes a few minutes): onPlayers is called with each better list, every 10 s,
  // until the list is complete. Returns a stop function.
  watchRosters: (onPlayers, onError) => {
    let stopped = false, timer = null;
    const tick = async (tries) => {
      try {
        const res = await api.fetchRosters();
        const players = res?.players || [];
        if (stopped) return;
        onPlayers(players, !!res?.isUpdating);
        if ((res?.isUpdating || players.length < 700) && tries < 40) timer = setTimeout(() => tick(tries + 1), 10000);
      } catch (e) {
        if (stopped) return;
        onError?.(e);
        if (tries < 40) timer = setTimeout(() => tick(tries + 1), 10000);
      }
    };
    tick(0);
    return () => { stopped = true; clearTimeout(timer); };
  },

  // Fetch NHL playoff teams (top 8 per conference, excludes eliminated)
  fetchPlayoffTeams: async () => {
    const response = await fetch(`${API_BASE}/api/nhl/playoff-teams`);
    if (!response.ok) return { teams: [] };
    return response.json();
  },

  // Fetch aggregate NHL injuries (map of team → [{name, status, injury, ...}])
  fetchAllInjuries: async () => {
    const response = await fetch(`${API_BASE}/api/nhl/injuries-all`);
    if (!response.ok) return { injuries: {} };
    return response.json();
  },

  // Free injury replacement (the server checks the injury report)
  injurySwap: async (poolId, dropId, add, accessToken) => {
    const response = await fetch(`${API_BASE}/api/pool/${poolId}/injury-swap`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ dropId, add: { id: add.id, name: add.name, team: add.team, position: add.position } }),
    });
    return response.json().catch(() => ({}));
  },

  // Fetch MLB games
  fetchMLBGames: async (dates) => {
    const response = await fetch(`${API_BASE}/api/mlb-games?dates=${dates.join(',')}`);
    if (!response.ok) return [];
    const json = await response.json();
    return unwrapArray(json);
  },

  // Fetch MLB team roster (batters + pitchers)
  fetchMLBRoster: async (teamId) => {
    const response = await fetch(`${API_BASE}/api/mlb-roster/${teamId}`);
    if (!response.ok) return { batters: [], pitchers: [] };
    return response.json();
  },

  // Fetch MLB game details for bet settlement
  // NFL: this week's games, a game's players (with their rates), and a game's results
  fetchNFLGames: async () => {
    const response = await fetch(`${API_BASE}/api/nfl-games`);
    if (!response.ok) return null;
    return response.json();
  },
  fetchNFLProps: async (home, away) => {
    const response = await fetch(`${API_BASE}/api/nfl-props/${home}/${away}`);
    if (!response.ok) return null;
    return response.json();
  },
  fetchNFLGameStats: async (gameId) => {
    const response = await fetch(`${API_BASE}/api/nfl-game-stats/${gameId}`);
    if (!response.ok) return null;
    return response.json();
  },
  // league leaders (Players tab) for nhl / nba / mlb / nfl
  fetchLeaders: async (sport) => {
    const response = await fetch(`${API_BASE}/api/leaders/${sport}`);
    if (!response.ok) return null;
    return response.json();
  },
  // standings for nhl / nba / mlb / nfl
  fetchStandings: async (sport) => {
    const response = await fetch(`${API_BASE}/api/standings/${sport}`);
    if (!response.ok) return null;
    return response.json();
  },
  fetchGameRecap: async (sport, gameId, teams = {}) => {
    const response = await fetch(`${API_BASE}/api/game-recap/${sport}/${gameId}?home=${encodeURIComponent(teams.home || '')}&away=${encodeURIComponent(teams.away || '')}`);
    if (!response.ok) return null;
    return response.json();
  },
  fetchF1Standings: async () => {
    const response = await fetch(`${API_BASE}/api/f1/standings`);
    if (!response.ok) return null;
    return response.json();
  },
  fetchF1Live: async (gameId) => {
    const response = await fetch(`${API_BASE}/api/f1-live/${gameId}`);
    if (!response.ok) return null;
    return response.json();
  },
  fetchNFLGameDetails: async (gameId) => {
    const response = await fetch(`${API_BASE}/api/nfl-game-details/${gameId}`);
    if (!response.ok) return null;
    return response.json();
  },

  // F1: the last race + the next ones (with odds for the open one), and a race's results
  fetchF1Races: async () => {
    const response = await fetch(`${API_BASE}/api/f1/races`);
    if (!response.ok) return null;
    return response.json();
  },
  fetchF1Details: async (gameId) => {
    const response = await fetch(`${API_BASE}/api/f1-details/${gameId}`);
    if (!response.ok) return null;
    return response.json();
  },

  fetchMLBGameDetails: async (gameId) => {
    const response = await fetch(`${API_BASE}/api/mlb-game-details/${gameId}`);
    if (!response.ok) return null;
    return response.json();
  },

  // Fetch MLB live team stats
  fetchMLBGameStats: async (gameId) => {
    const response = await fetch(`${API_BASE}/api/mlb-game-stats/${gameId}`);
    if (!response.ok) throw new Error('Failed to fetch MLB game stats');
    return response.json();
  },

  // Fetch NBA games
  fetchNBAGames: async (dates) => {
    const response = await fetch(`${API_BASE}/api/nba-games?dates=${dates.join(',')}`);
    if (!response.ok) return [];
    const json = await response.json();
    return unwrapArray(json);
  },

  // Fetch NBA team roster
  // players' per-game rates for the odds engine: nhl, nba, mlb/<teamId>
  fetchPlayerRates: async (path) => {
    const response = await fetch(`${API_BASE}/api/player-rates/${path}`);
    if (!response.ok) return null;
    return response.json();
  },

  fetchNBARoster: async (teamId) => {
    const response = await fetch(`${API_BASE}/api/nba-roster/${teamId}`);
    if (!response.ok) return { players: [] };
    return response.json();
  },

  // Fetch NBA game details for bet settlement
  fetchNBAGameDetails: async (gameId) => {
    const response = await fetch(`${API_BASE}/api/nba-game-details/${gameId}`);
    if (!response.ok) return null;
    return response.json();
  },

  // Fetch NBA live team stats
  // teams = { home, away } (tricodes): lets the server find the game on ESPN when nba.com refuses
  fetchNBAGameStats: async (gameId, teams) => {
    const response = await fetch(`${API_BASE}/api/nba-game-stats/${gameId}${teams ? `?home=${encodeURIComponent(teams.home)}&away=${encodeURIComponent(teams.away)}` : ''}`);
    if (!response.ok) throw new Error('Failed to fetch NBA game stats');
    return response.json();
  },

  // Fetch scout report
  fetchScoutReport: async (forceRefresh = false) => {
    const url = `${API_BASE}/api/scout-report${forceRefresh ? '?refresh=1' : ''}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch scout report');
    return response.json();
  },

  // Fetch pool standings with per-player breakdowns
  // Today's NHL games by team abbrev: { teams: { MTL: { started, startTimeUTC, opponent, home } } }
  fetchNhlToday: async () => {
    const response = await fetch(`${API_BASE}/api/nhl-today`);
    if (!response.ok) throw new Error('Failed to fetch today\'s games');
    return response.json();
  },

  fetchPoolStandings: async (poolId) => {
    const response = await fetch(`${API_BASE}/api/pool/${poolId}/standings`);
    if (!response.ok) throw new Error('Failed to fetch standings');
    return response.json();
  },

  // GM Season trades: nights each NHL team shares with each member's team, CPU styles
  fetchTradeInfo: async (poolId) => {
    const response = await fetch(`${API_BASE}/api/pool/${poolId}/trade-info`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Failed to fetch trade info');
    return response.json();
  },

fetchTradePreview: async (poolId, to, give, get) => {
    const q = new URLSearchParams({ to, give: give.join(','), get: get.join(',') });
    const response = await fetch(`${API_BASE}/api/pool/${poolId}/trade-preview?${q}`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Failed to fetch preview');
    return response.json();
  },
  fetchTradeValues: async (poolId, to, from) => {
    const response = await fetch(`${API_BASE}/api/pool/${poolId}/trade-values?to=${to}&from=${from}`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Failed to fetch values');
    return response.json();
  },
  fetchTradeForm: async (poolId, users) => {
    const response = await fetch(`${API_BASE}/api/pool/${poolId}/trade-form?users=${users.join(',')}`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Failed to fetch form');
    return response.json();
  },

  fetchSeasonGame: async (poolId, gameId) => {
    const response = await fetch(`${API_BASE}/api/pool/${poolId}/season/game/${gameId}`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Failed to fetch game');
    return response.json();
  },

  // Bets are settled by the server: ask it to check this player's pending bets now
  settleMyBets: async (userId) => {
    const response = await fetch(`${API_BASE}/api/bets/settle`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId }) });
    if (!response.ok) throw new Error('Failed to settle bets');
    return response.json();
  },

  // one NHL player's profile (lineup stats mode)
  fetchNhlPlayer: async (nhlId) => {
    const response = await fetch(`${API_BASE}/api/nhl/player/${nhlId}`);
    if (!response.ok) throw new Error('Failed to fetch player');
    return response.json();
  },

  // GM Season pools: league table, game log, upcoming matchups
  fetchPoolSeason: async (poolId) => {
    const response = await fetch(`${API_BASE}/api/pool/${poolId}/season`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Failed to fetch season');
    return response.json();
  },

  // Check if API is available
  isAvailable: () => {
    return isElectron() || true; // Web API is always "available" (will fail on fetch if not)
  }
};

export default api;
